import React, { useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { supabase } from './supabaseClient';
import { FaSearch, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';

const AHTOpsBatchLookup = () => {
  const [batches, setBatches] = useState([]);
  const [batchSearch, setBatchSearch] = useState('');
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordColumns, setRecordColumns] = useState([]);
  const [recordSearch, setRecordSearch] = useState('');
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);

  const fetchBatches = async () => {
    try {
      setIsLoadingBatches(true);

      // 1) Find which batch IDs exist in the AHT Ops staging table.
      // (We reuse mvw_raw.mvw_call_upload_batches for control, so we must filter.)
      const { data: batchIdRows, error: batchIdError } = await supabase
        .schema('mvw_raw')
        .from('mvw_aht_ops_staging')
        .select('batch_id');

      if (batchIdError) throw batchIdError;

      const batchIds = Array.from(
        new Set((batchIdRows || []).map((r) => r.batch_id).filter(Boolean))
      );

      if (batchIds.length === 0) {
        setBatches([]);
        return;
      }

      // 2) Load batches metadata
      const { data, error } = await supabase
        .schema('mvw_raw')
        .from('mvw_call_upload_batches')
        .select('*')
        .in('id', batchIds)
        .order('id', { ascending: false });

      if (error) throw error;

      const rowsWithId = (data || []).map((row, index) => ({
        id: row.id ?? index,
        ...row,
      }));

      setBatches(rowsWithId);
    } catch (err) {
      console.error('Error loading AHT Ops batches:', err);
      toast.error(`Error al cargar lotes AHT Ops: ${err.message}`);
    } finally {
      setIsLoadingBatches(false);
    }
  };

  const fetchRecordsForBatch = async (batchId) => {
    try {
      setIsLoadingRecords(true);
      const { data, error } = await supabase
        .schema('mvw_raw')
        .from('mvw_aht_ops_staging')
        .select('*')
        .eq('batch_id', batchId)
        .order('id', { ascending: true });

      if (error) throw error;

      const rowsWithId = (data || []).map((row, index) => ({
        id: row.id ?? index,
        ...row,
      }));

      if (rowsWithId.length > 0) {
        const cols = Object.keys(rowsWithId[0]).map((key) => ({
          field: key,
          headerName: key,
          flex: 1,
          minWidth: 150,
        }));
        setRecordColumns(cols);
      } else {
        setRecordColumns([]);
      }

      setRecords(rowsWithId);
    } catch (err) {
      console.error('Error loading AHT Ops batch records:', err);
      toast.error(`Error al cargar registros del lote AHT Ops: ${err.message}`);
    } finally {
      setIsLoadingRecords(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  const filteredBatches = useMemo(() => {
    if (!batchSearch) return batches;
    return batches.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(batchSearch.toLowerCase())
      )
    );
  }, [batches, batchSearch]);

  const filteredRecords = useMemo(() => {
    if (!recordSearch) return records;
    return records.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(recordSearch.toLowerCase())
      )
    );
  }, [records, recordSearch]);

  const handleBatchClick = (params) => {
    const batchId = params.row.id;
    setSelectedBatchId(batchId);
    fetchRecordsForBatch(batchId);
  };

  return (
    <div className="form-card">
      <h2>Consulta de Lotes (AHT Ops)</h2>
      <p className="form-description">
        Seleccione un lote para visualizar los registros cargados a <code>mvw_aht_ops_staging</code>.
      </p>

      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 420px', minWidth: '320px' }}>
          <h3 style={{ marginBottom: '0.75rem' }}>Lotes</h3>
          <div className="search-bar-container batch-search-center">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Buscar lote..."
                value={batchSearch}
                onChange={(e) => setBatchSearch(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="batch-card-list">
            {isLoadingBatches && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#007bff', padding: '0.5rem 0' }}>
                <FaSpinner className="spinner" /> Cargando lotes...
              </div>
            )}

            {!isLoadingBatches && filteredBatches.length === 0 && (
              <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>No hay lotes para mostrar.</div>
            )}

            {filteredBatches.map((batch) => {
              const isSelected = selectedBatchId === batch.id;
              const createdAt = batch.created_at ? new Date(batch.created_at).toLocaleString() : 'N/D';
              return (
                <button
                  type="button"
                  key={batch.id}
                  className={`batch-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleBatchClick({ row: batch })}
                >
                  <div className="batch-card-title">Lote #{batch.id}</div>
                  <div className="batch-card-meta">
                    <div>
                      <span className="meta-label">Archivo</span>
                      <span className="meta-value">{batch.file_name || 'N/D'}</span>
                    </div>
                    <div>
                      <span className="meta-label">Subido por</span>
                      <span className="meta-value">{batch.uploaded_by || 'N/D'}</span>
                    </div>
                    <div>
                      <span className="meta-label">Procesados</span>
                      <span className="meta-value">{batch.records_processed ?? 'N/D'}</span>
                    </div>
                    <div>
                      <span className="meta-label">Fecha</span>
                      <span className="meta-value">{createdAt}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ flex: '1 1 520px', minWidth: '320px' }}>
          <h3 style={{ marginBottom: '0.75rem' }}>
            {selectedBatchId ? `Registros del Lote #${selectedBatchId}` : 'Registros del Lote'}
          </h3>
          {isLoadingRecords && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#007bff' }}>
              <FaSpinner className="spinner" /> Cargando registros...
            </div>
          )}
          <div className="search-bar-container batch-search-center">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Buscar en registros..."
                value={recordSearch}
                onChange={(e) => setRecordSearch(e.target.value)}
                className="search-input"
                disabled={!selectedBatchId}
              />
            </div>
          </div>
          <div className="grid-container">
            <DataGrid
              rows={filteredRecords}
              columns={recordColumns}
              loading={isLoadingRecords}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 },
                },
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              density="compact"
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
              }
              sx={{
                border: 0,
                fontSize: '0.8rem',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f8f9fa',
                  color: '#495057',
                  fontWeight: '600',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  borderBottom: '1px solid #dee2e6',
                },
                '& .MuiDataGrid-columnSeparator': {
                  display: 'none',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: 'none',
                },
                '& .even-row': {
                  backgroundColor: '#ffffff',
                },
                '& .odd-row': {
                  backgroundColor: '#f8f9fa',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#e9ecef',
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AHTOpsBatchLookup;

